import { useEffect, useState, type ComponentType, type MouseEvent } from 'react';
import { Link as RouterLink, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import Header from '@carbon/react/es/components/UIShell/Header';
import HeaderName from '@carbon/react/es/components/UIShell/HeaderName';
import HeaderMenuButton from '@carbon/react/es/components/UIShell/HeaderMenuButton';
import HeaderGlobalBar from '@carbon/react/es/components/UIShell/HeaderGlobalBar';
import HeaderGlobalAction from '@carbon/react/es/components/UIShell/HeaderGlobalAction';
import SkipToContent from '@carbon/react/es/components/UIShell/SkipToContent';
import SideNav from '@carbon/react/es/components/UIShell/SideNav';
import SideNavItems from '@carbon/react/es/components/UIShell/SideNavItems';
import ChevronDown from '@carbon/icons-react/es/ChevronDown';
import Dashboard from '@carbon/icons-react/es/Dashboard';
import CloudUpload from '@carbon/icons-react/es/CloudUpload';
import MachineLearningModel from '@carbon/icons-react/es/MachineLearningModel';
import Rocket from '@carbon/icons-react/es/Rocket';
import Radar from '@carbon/icons-react/es/Radar';
import Notification from '@carbon/icons-react/es/Notification';
import AiGenerate from '@carbon/icons-react/es/AiGenerate';
import Settings from '@carbon/icons-react/es/Settings';
import Logout from '@carbon/icons-react/es/Logout';
import Light from '@carbon/icons-react/es/Light';
import Asleep from '@carbon/icons-react/es/Asleep';

import { useAuthStore } from '../auth/store.js';
import { useThemeStore } from '../theme/store.js';
import { useI18n, type MessageKey } from '../i18n/index.js';
import { GlobalSearch } from '../features/search/GlobalSearch.js';
import { AssistantPanel } from '../features/assistant/AssistantPanel.js';
import { LanguageMenu } from '../features/language/LanguageMenu.js';
import { OnboardingHost } from '../onboarding/OnboardingHost.js';
import { useTourStore } from '../onboarding/tour-store.js';

/**
 * Les quatre grandes fonctionnalités (PRD NFR-27). Libellés courts : le rail est étroit et pose le
 * texte, en capitales grasses, sous l'icône.
 */
interface NavItem {
  to: string;
  label: string;
  icon: ComponentType;
  /** Ancre de visite guidée (ADR-0031) : sert d'`data-tour` sur le lien de la catégorie. */
  tourId: string;
  /** Sous-catégories (ancres dans la page) affichées sous l'entrée dans la barre. */
  sub?: { hash: string; labelKey: MessageKey }[];
}

const NAV_ITEMS: NavItem[] = [
  {
    to: '/',
    label: 'Overview',
    icon: Dashboard,
    tourId: 'nav-overview',
    sub: [
      { hash: 'overview-videos', labelKey: 'overview.recentVideos' },
      { hash: 'overview-models', labelKey: 'overview.evaluatedModels' },
    ],
  },
  {
    to: '/data',
    label: 'Data',
    icon: CloudUpload,
    tourId: 'nav-data',
    sub: [
      { hash: 'data-videos', labelKey: 'data.section.videos' },
      { hash: 'data-images', labelKey: 'data.section.images' },
      { hash: 'data-simulation', labelKey: 'data.section.simulations' },
    ],
  },
  {
    to: '/model',
    label: 'Model',
    icon: MachineLearningModel,
    tourId: 'nav-model',
    sub: [
      { hash: 'model-zoo', labelKey: 'model.section.modelZoo' },
      { hash: 'construire', labelKey: 'model.section.build' },
    ],
  },
  {
    to: '/deploy',
    label: 'Deploy',
    icon: Rocket,
    tourId: 'nav-deploy',
    sub: [
      { hash: 'deploy-compute', labelKey: 'deploy.computeLabel' },
      { hash: 'deploy-hardware', labelKey: 'deploy.hardwareLabel' },
    ],
  },
];

/**
 * Entrée « Ops » (vue opérationnelle counter-UAS, ADR-0040). Tenue **hors** du rail standard : la
 * navigation devient dépendante du persona (ADR-0040 §4). L'opérateur terrain ne voit **que** cette
 * vue (IHM curée, pas les domaines donnée/modèle/déploiement) ; elle n'est proposée aux autres que
 * s'ils administrent l'organisation. Ce n'est **pas** une frontière de sécurité (l'API reste gardée
 * par `OrgRole`), juste un atelier ciblé — conformément à ADR-0031 (le persona n'est pas un droit).
 */
const OPS_ITEM: NavItem = {
  to: '/ops',
  label: 'Ops',
  icon: Radar,
  tourId: 'nav-ops',
};

/** Rail visible selon le persona : l'opérateur n'a que « Ops » ; les admins l'ont en plus des quatre. */
function navItemsFor(persona: string | null | undefined, isOrgAdmin: boolean): NavItem[] {
  if (persona === 'OPERATOR') return [OPS_ITEM];
  return isOrgAdmin ? [...NAV_ITEMS, OPS_ITEM] : NAV_ITEMS;
}

/**
 * Initiales pour la pastille de profil : première lettre des deux premiers mots du nom affiché
 * (« Jean-Loup Ployer » → « JL »). Le trait d'union sépare comme l'espace, pour couvrir les
 * prénoms composés. À défaut de nom, on retombe sur la première lettre du repli (l'e-mail).
 */
function accountInitials(displayName: string | undefined, fallback: string): string {
  const source = (displayName ?? '').trim();
  const parts = source.split(/[\s-]+/).filter(Boolean);
  const letters = parts
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('');
  return (letters || source.charAt(0) || fallback.charAt(0) || '?').toUpperCase();
}

/** Marque AerialViews : chevrons d'altitude, sommet en accent orange (logo retenu). */
function BrandMark() {
  return (
    <svg
      className="ddp-brand__mark"
      viewBox="0 0 32 32"
      width="24"
      height="24"
      fill="none"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="7,24 16,18 25,24" stroke="currentColor" />
      <polyline points="7,13 16,7 25,13" stroke="var(--av-accent)" />
    </svg>
  );
}

/**
 * Coquille des pages authentifiées : en-tête + **navigation latérale** à quatre entrées.
 *
 * S'appuie sur l'`UIShell` de Carbon (NFR-22). La navigation est **persistante** en desktop,
 * repliée derrière le **hamburger** sous `lg`. Le bas de la barre porte le compte (profil,
 * réglages, déconnexion) ; le haut à droite est réservé à l'assistant IA et aux notifications.
 */
export function AppLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const currentUser = useAuthStore((state) => state.currentUser);
  const logout = useAuthStore((state) => state.logout);
  const organization = currentUser?.memberships[0];
  const isOrgAdmin = organization?.role === 'ADMIN' || organization?.role === 'OWNER';
  const navItems = navItemsFor(currentUser?.persona, isOrgAdmin);
  const themeMode = useThemeStore((state) => state.mode);
  const toggleTheme = useThemeStore((state) => state.toggle);
  const { t } = useI18n();
  const [navExpanded, setNavExpanded] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  // Une visite guidée ancrée sur le rail force l'ouverture du tiroir le temps de la visite, pour que
  // ses coachmarks pointent des entrées visibles (ADR-0031) — **uniquement sous `lg`**, là où le
  // rail est replié ; sur desktop il est déjà persistant, on n'y touche pas.
  const railTourOpen = useTourStore((state) => state.railOpen);
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1055px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1055px)');
    const onChange = () => setIsNarrow(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const navShown = navExpanded || (railTourOpen && isNarrow);
  // Dépliage des sections du rail : indépendant de la navigation. Toutes dépliées par défaut ;
  // le repli/dépli se fait **au chevron uniquement** (cliquer la catégorie navigue vers la page).
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(NAV_ITEMS.map((item) => [item.to, true])),
  );
  const toggleSection = (to: string) =>
    setOpenSections((sections) => ({ ...sections, [to]: !(sections[to] ?? true) }));

  const onLogout = () => {
    void logout();
    void navigate({ to: '/login' });
  };

  const isActive = (to: string): boolean =>
    to === '/' ? pathname === '/' : pathname.startsWith(to);

  // Navigation SPA : on empêche le rechargement et on referme le tiroir mobile après le clic.
  const go = (to: string) => (event: MouseEvent) => {
    event.preventDefault();
    setNavExpanded(false);
    void navigate({ to });
  };

  // Sous-catégorie : va sur la page puis défile jusqu'à l'ancre (le routeur ne défile pas seul vers
  // un hash). rAF laisse la page se monter avant de chercher la cible.
  const goAnchor = (to: string, hash: string) => (event: MouseEvent) => {
    event.preventDefault();
    setNavExpanded(false);
    void navigate({ to }).then(() => {
      requestAnimationFrame(() => {
        // Poser le fragment natif déclenche `hashchange`, seul signal auquel s'accrochent les
        // pages à sections (Model, ADR-0017) pour changer de section : `navigate` passe par
        // `pushState` et n'émet pas cet événement. Le défilement suit pour les ancres réelles.
        if (window.location.hash !== `#${hash}`) {
          window.location.hash = hash;
        }
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  };

  const initials = accountInitials(currentUser?.displayName, currentUser?.email ?? '?');

  return (
    <>
      <Header aria-label="AerialViews">
        <SkipToContent />
        <HeaderMenuButton
          aria-label={navExpanded ? t('nav.close') : t('nav.open')}
          onClick={() => setNavExpanded((value) => !value)}
          isActive={navExpanded}
          aria-expanded={navExpanded}
        />
        <HeaderName href="/" prefix="" onClick={go('/')}>
          <span className="ddp-brand">
            <BrandMark />
            AerialViews
          </span>
        </HeaderName>

        {/* Recherche transverse (ADR-0015), à droite du logo. */}
        <GlobalSearch />

        {/* Assistant IA (langage naturel) et notifications de la plateforme. */}
        <HeaderGlobalBar>
          <HeaderGlobalAction
            aria-label={t('account.assistant')}
            tooltipAlignment="end"
            isActive={assistantOpen}
            onClick={() => setAssistantOpen((value) => !value)}
          >
            <AiGenerate size={20} />
          </HeaderGlobalAction>
          <HeaderGlobalAction aria-label={t('account.notifications')} tooltipAlignment="end">
            <Notification size={20} />
          </HeaderGlobalAction>
          {/* Sélecteur de langue (menu unique), à droite des notifications. */}
          <LanguageMenu />
        </HeaderGlobalBar>

        <SideNav
          className="ddp-nav"
          aria-label="Navigation principale"
          expanded={navShown}
          onOverlayClick={() => setNavExpanded(false)}
          isPersistent
        >
          <SideNavItems>
            {navItems.map((item) => {
              const active = isActive(item.to);
              const open = openSections[item.to] ?? true;
              const Icon = item.icon;
              return (
                <li
                  key={item.to}
                  className={`cds--side-nav__item ddp-nav-section${
                    active ? ' cds--side-nav__item--active' : ''
                  }`}
                >
                  {/* Cliquer la **catégorie** navigue vers la page ; le dépliage des sous-sections
                      est réservé au **chevron** (bouton séparé), pour ne pas piéger l'accès à la page. */}
                  <div className="ddp-nav-section__head">
                    <a
                      href={item.to}
                      className="cds--side-nav__link ddp-nav-section__link"
                      aria-current={active ? 'page' : undefined}
                      data-tour={item.tourId}
                      onClick={go(item.to)}
                    >
                      <span className="cds--side-nav__icon">
                        <Icon />
                      </span>
                      <span className="cds--side-nav__submenu-title">{item.label}</span>
                    </a>
                    {item.sub && (
                      <button
                        type="button"
                        className="ddp-nav-section__toggle"
                        aria-label={open ? t('nav.collapseSection') : t('nav.expandSection')}
                        aria-expanded={open}
                        onClick={() => toggleSection(item.to)}
                      >
                        <ChevronDown className="ddp-nav-section__chevron" />
                      </button>
                    )}
                  </div>
                  {item.sub && open && (
                    <ul className="cds--side-nav__menu">
                      {item.sub.map((s) => (
                        <li key={s.hash} className="cds--side-nav__menu-item">
                          <a
                            href={`${item.to}#${s.hash}`}
                            className="cds--side-nav__link"
                            onClick={goAnchor(item.to, s.hash)}
                          >
                            <span className="cds--side-nav__link-text">{t(s.labelKey)}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </SideNavItems>

          {/* Pied de la barre : compte de l'utilisateur, poussé en bas (voir app.scss). */}
          <div className="ddp-nav-account" data-tour="account">
            {organization && (
              <span className="ddp-nav-account__org">{organization.organizationName}</span>
            )}
            <div className="ddp-nav-account__row">
              <RouterLink
                to="/profil"
                className="ddp-avatar"
                title={currentUser?.displayName ?? t('account.profile')}
                aria-label={t('account.profile')}
              >
                {initials}
              </RouterLink>
            </div>
            <div className="ddp-nav-account__actions">
              {/* Bascule jour/nuit ici même : la préférence existait déjà (profil), mais l'accès
                  rapide manquait. L'icône montre la cible — soleil quand on est en nuit, lune sinon. */}
              <button
                type="button"
                className="ddp-nav-account__btn"
                onClick={toggleTheme}
                aria-label={
                  themeMode === 'dark' ? t('account.themeToLight') : t('account.themeToDark')
                }
                title={themeMode === 'dark' ? t('account.themeToLight') : t('account.themeToDark')}
              >
                {themeMode === 'dark' ? <Light size={18} /> : <Asleep size={18} />}
              </button>
              <RouterLink
                to="/profil"
                className="ddp-nav-account__btn"
                aria-label={t('account.settings')}
                title={t('account.settings')}
              >
                <Settings size={18} />
              </RouterLink>
              <button
                type="button"
                className="ddp-nav-account__btn"
                onClick={onLogout}
                aria-label={t('account.logout')}
                title={t('account.logout')}
              >
                <Logout size={18} />
              </button>
            </div>
          </div>
        </SideNav>
      </Header>

      <main id="main-content" className="ddp-page ddp-page--with-nav">
        <Outlet />
      </main>

      <AssistantPanel open={assistantOpen} onClose={() => setAssistantOpen(false)} />

      {/* Onboarding (ADR-0031) : modale persona à la 1re connexion + visites guidées. Monté ici, dans
          la coquille, pour ancrer les visites sur des éléments toujours présents (le rail). */}
      <OnboardingHost />
    </>
  );
}
